import React from "react";
import { Link } from "react-router-dom";
import { Truck, RotateCcw, Headphones, CreditCard, ArrowRight } from "lucide-react";
import CategorySidebar from "../components/CategorySidebar";
import HeroSlider from "../components/HeroSlider";
import CountdownTimer from "../components/CountdownTimer";
import ProductCard from "../components/ProductCard";
import Testimonials from "../components/Testimonials";
import { PRODUCTS, PROMO_BANNERS, CATEGORIES } from "../data/mock";

const Section = ({ title, action, children }) => (
  <section className="mt-16">
    <div className="flex items-end justify-between mb-6 border-b border-zinc-200 pb-4">
      <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const HomePage = () => {
  const bestSellers = PRODUCTS.filter((p) => p.rating >= 4.8).slice(0, 6);
  const dealProduct = PRODUCTS.find((p) => p.slug === "spot-uv-business-cards");

  return (
    <main className="bg-white">
      {/* Top hero with sidebar */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          <CategorySidebar />
          <div className="flex-1 min-w-0">
            <HeroSlider />
          </div>
        </div>
      </div>

      {/* Feature row */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 border-y border-zinc-100 py-6">
          {[
            { icon: Truck, title: "Local Delivery", desc: "Free over $65 in Broward." },
            { icon: RotateCcw, title: "Reprint Promise", desc: "Within 30 days if it's our error." },
            { icon: Headphones, title: "Online Support", desc: "Real humans, not bots." },
            { icon: CreditCard, title: "Flexible Payment", desc: "All major cards & Affirm." },
          ].map((f) => (
            <div key={f.title} className="flex items-center gap-3 px-3">
              <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center shrink-0">
                <f.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-zinc-900">{f.title}</div>
                <div className="text-xs text-zinc-500">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Promo banners row */}
      <div className="max-w-7xl mx-auto px-4 mt-8">
        <div className="grid md:grid-cols-3 gap-5">
          {PROMO_BANNERS.map((b) => (
            <Link key={b.title} to={b.href} className={`${b.bg} rounded-lg p-7 flex items-center gap-5 group border border-zinc-100 hover:shadow-lg transition-all`}>
              <img src={b.img} alt={b.title} className="w-24 h-24 object-cover rounded-md group-hover:scale-105 transition-transform" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-rose-500 mb-1">{b.label}</div>
                <div className="text-lg font-bold text-zinc-900 leading-snug mb-3">{b.title}</div>
                <span className="text-xs font-semibold text-zinc-700 group-hover:text-rose-500 inline-flex items-center gap-1 transition-colors">
                  Shop Now <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Deal of the day */}
      <div className="max-w-7xl mx-auto px-4">
        <Section title="Deal of The Day" action={<CountdownTimer targetHours={36} />}>
          {dealProduct && (
            <div className="grid md:grid-cols-2 gap-8 items-center bg-gradient-to-br from-amber-50 to-rose-50 rounded-xl p-8 md:p-12">
              <div className="aspect-square bg-white rounded-xl overflow-hidden">
                <img src={dealProduct.image} alt={dealProduct.name} className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="inline-block text-xs uppercase tracking-widest font-bold text-rose-500 bg-rose-100 px-3 py-1 rounded-full mb-4">Limited Offer</span>
                <h3 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-3">{dealProduct.name}</h3>
                <p className="text-zinc-600 leading-relaxed mb-6">{dealProduct.description}</p>
                <div className="flex items-baseline gap-3 mb-6">
                  <span className="text-4xl font-bold text-rose-500">${dealProduct.fromPrice}</span>
                  <span className="text-lg text-zinc-400 line-through">${(dealProduct.fromPrice * 1.4).toFixed(2)}</span>
                  <span className="text-sm font-semibold text-emerald-600">Save 30%</span>
                </div>
                <Link to={`/product/${dealProduct.slug}`} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-7 py-3 rounded-md text-sm font-semibold hover:bg-rose-500 transition-colors">
                  Configure & Order <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </Section>

        {/* Popular Categories */}
        <Section title="Popular Categories" action={<Link to="/shop" className="text-sm font-semibold text-rose-500 hover:underline">View All Products</Link>}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {CATEGORIES.map((c) => (
              <Link key={c.slug} to={`/category/${c.slug}`} className="group block">
                <div className="aspect-square bg-zinc-50 rounded-lg overflow-hidden border border-zinc-100 group-hover:shadow-lg transition-all">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="mt-3 text-center">
                  <h3 className="text-sm font-semibold text-zinc-900 group-hover:text-rose-500 transition-colors">{c.name}</h3>
                  <span className="text-xs text-zinc-500">{PRODUCTS.filter(p => p.category === c.slug).length} products</span>
                </div>
              </Link>
            ))}
          </div>
        </Section>

        {/* Testimonials */}
        <Section title="What Customers Say">
          <Testimonials />
        </Section>

        {/* Best Seller */}
        <Section title="Best Sellers" action={<Link to="/shop" className="text-sm font-semibold text-rose-500 hover:underline">Shop all</Link>}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {bestSellers.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </Section>

        {/* Newsletter */}
        <div className="mt-20 mb-8 rounded-xl bg-gradient-to-br from-zinc-900 to-zinc-800 text-white p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <span className="inline-block text-xs uppercase tracking-widest font-bold bg-rose-500 px-3 py-1 rounded-full mb-4">New to Design Plus? Get 20% off</span>
            <h3 className="text-3xl md:text-4xl font-bold leading-tight">Sign up for special offers, news and inspiration</h3>
            <p className="text-zinc-400 mt-3">Join the Design Plus newsletter for 20% off your first order, design tips and exclusive offers.</p>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); alert("Subscribed! Check your inbox for the 20% off code."); }} className="flex flex-col sm:flex-row gap-3">
            <input type="email" required placeholder="you@brand.com" className="flex-1 px-5 py-3.5 rounded-md text-zinc-900 outline-none focus:ring-2 focus:ring-rose-400" />
            <button className="bg-rose-500 hover:bg-rose-600 px-6 py-3.5 rounded-md text-sm font-bold transition-colors">Sign up</button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default HomePage;
